import AdvancedFilterController from '../../pos_controllers/products/advanced_filter_controller'

export default class extends AdvancedFilterController {
  static targets = ['searchClauseContainer', 'addClauseButton', 'resetFiltersButton', 'filterSelector']

  initialize() {
    super.initialize()
    this.boundHandleSearchableSelectChange = this.handleSearchableSelectChange.bind(this)
    this.boundHandleFilterTypeChange = this.handleFilterTypeChangeFromSelect.bind(this)
    this.boundHandleMultiSelectApplied = this.handleMultiSelectApplied.bind(this)
    this.isUpdatingClause = false
    // Track pending multi-select updates to prevent race conditions
    this.pendingMultiSelectUpdates = 0
  }

  connect() {
    this.lockedDateKey = this.element.dataset.selectAdvancedFilterLockedDateKeyValue || null
    this.searchOptions = JSON.parse(this.element.dataset.selectAdvancedFilterSearchOptionsValue || '{}')

    const existingClauses = JSON.parse(this.element.dataset.selectAdvancedFilterSearchClausesAttributesValue || '[]')

    const filteredExisting = Array.isArray(existingClauses) ? existingClauses.filter(clause => clause && clause.search_key) : []
    if (filteredExisting.length > 0) {
      this.searchClauses = filteredExisting.map(clause => this.initializeSearchClause(clause))
    } else {
      const initialSearchKeys = this.element.dataset.selectAdvancedFilterInitialSearchKeysValue || ''
      this.addInitialSearchClauses(initialSearchKeys)
    }

    const existingPills = this.searchClauseContainerTarget.querySelectorAll('[data-clause-index]')
    if (existingPills.length === 0) {
      // Always render search clauses from JS to ensure locked date key is properly applied
      this.renderSearchClauses()

      // After rendering, extract labels on next frame for minimal flash
      requestAnimationFrame(() => {
        this.updateSimpleSelectLabelsFromDOM()
      })
    }

    this.updateResetButtonVisibility()
    this.setupSearchFieldListener()
    this.setupFilterSelector()

    // Store reference globally for modal callbacks
    if (!this.element.id) {
      this.element.id = 'select--advanced-filter'
    }
    window.advancedFilterController = this

    // Setup global click listener to close dropdowns when clicking outside
    this.setupGlobalClickListener()

    // Listen for multi-select applied events
    this.element.addEventListener('multi-select-applied', this.boundHandleMultiSelectApplied)
  }

  // Update simple select labels from DOM (for page reload with query params)
  updateSimpleSelectLabelsFromDOM() {
    const existingPills = this.searchClauseContainerTarget.querySelectorAll('[data-clause-index]')

    existingPills.forEach((pillElement) => {
      const clauseIndex = parseInt(pillElement.dataset.clauseIndex)
      const clause = this.searchClauses[clauseIndex]
      if (!clause) return

      const valuesLabel = pillElement.querySelector(`[data-values-label="${clauseIndex}"]`)
      if (!valuesLabel) return

      // Select elements are inside the valuesDropdown
      const valuesDropdown = pillElement.querySelector('[data-target*="filter-pill.valuesDropdown"]')
      if (!valuesDropdown) return

      // Find ALL simple select elements (not searchable selects)
      const allSelects = valuesDropdown.querySelectorAll('select[name*="[sta]"]:not([data-target*="searchable-select"])')

      const labels = []

      allSelects.forEach((select) => {
        if (select.selectedOptions && select.selectedOptions.length > 0) {
          const selectedOption = select.selectedOptions[0]
          const optionText = selectedOption.text
          const optionValue = selectedOption.value

          // Skip empty/placeholder options
          if (optionText && optionValue && optionText !== 'Choose an option') {
            labels.push(optionText)

            // Extract the field key from the select name (support both long [search_terms_attributes] and short [sta])
            const nameMatch = select.name.match(/\[(?:search_terms_attributes|sta)\]\[(\d+)\](?:\[values\]\[([^\]]+)\]|\[value\])/)
            if (nameMatch) {
              const termIndex = parseInt(nameMatch[1])
              const fieldKey = nameMatch[2] || 'value'

              // Update the clause data
              if (clause.search_terms && clause.search_terms[termIndex]) {
                if (!clause.search_terms[termIndex].selected_texts) {
                  clause.search_terms[termIndex].selected_texts = {}
                }
                clause.search_terms[termIndex].selected_texts[fieldKey] = optionText
              }
            }
          }
        }
      })

      // Update the pill label display if we found labels
      if (labels.length > 0) {
        if (labels.length === 1) {
          valuesLabel.textContent = labels[0]
        } else {
          valuesLabel.innerHTML = `${labels[0]}, <span class="text-muted">+${labels.length - 1} more</span>`
        }
      }
    })
  }

  // Setup global click listener for closing dropdowns
  setupGlobalClickListener() {
    this.boundGlobalClickHandler = this.handleGlobalClick.bind(this)
    document.addEventListener('click', this.boundGlobalClickHandler, true)
  }

  // Handle clicks outside of pills to close dropdowns
  handleGlobalClick(event) {
    const clickedPill = event.target.closest('[data-controller*="select--filter-pill"]')
    const clickedMultiSelect = event.target.closest('[data-controller*="multi-searchable-select"]')

    if (!clickedPill && !clickedMultiSelect) {
      this.closeAllPillDropdowns()
    }
  }

  // Close all pill dropdowns
  closeAllPillDropdowns() {
    const pills = this.searchClauseContainerTarget.querySelectorAll('[data-controller*="select--filter-pill"]')
    pills.forEach(pillElement => {
      const controller = this.application.getControllerForElementAndIdentifier(
        pillElement,
        'select--filter-pill'
      )
      if (controller && controller.closeAllSegmentDropdowns) {
        controller.closeAllSegmentDropdowns()
      }
    })
  }

  disconnect() {
    // Remove global click listener
    if (this.boundGlobalClickHandler) {
      document.removeEventListener('click', this.boundGlobalClickHandler, true)
    }

    // Remove multi-select listener
    if (this.boundHandleMultiSelectApplied) {
      this.element.removeEventListener('multi-select-applied', this.boundHandleMultiSelectApplied)
    }

    // Clean up any timers
    if (this.submitTimer) {
      clearTimeout(this.submitTimer)
    }
    if (this.inputTimer) {
      clearTimeout(this.inputTimer)
    }

    // Clean up global reference
    if (window.advancedFilterController === this) {
      delete window.advancedFilterController
    }
  }

  // Handle filter type change from searchable select (bound handler for event listener)
  handleFilterTypeChangeFromSelect(event) {
    const hiddenSelect = event.target
    const newFilterType = hiddenSelect.value

    if (!newFilterType) return

    // Find the clause index from the field name
    const match = hiddenSelect.name.match(/\[(\d+)\]/)
    if (!match) return

    const clauseIndex = parseInt(match[1])

    this.updateFilterType(clauseIndex, newFilterType)
  }

  // Handle filter type change from searchable select
  handleFilterTypeChange(event) {
    const hiddenSelect = event.target
    const newFilterType = hiddenSelect.value

    if (!newFilterType) return

    // Find the clause index from the field name
    const match = hiddenSelect.name.match(/\[(\d+)\]/)
    if (!match) return

    const clauseIndex = parseInt(match[1])

    this.updateFilterType(clauseIndex, newFilterType)
  }

  updateFilterType(clauseIndex, newFilterType) {
    const clause = this.searchClauses[clauseIndex]
    if (!clause) return

    const searchOption = this.searchOptions[newFilterType]
    if (!searchOption) return

    clause.search_key = newFilterType

    let newComparator
    if (searchOption.type === 'date' && searchOption.comparators) {
      const hasBetween = searchOption.comparators.some(c => c[0] === 'between')
      newComparator = hasBetween ? 'between' : searchOption.comparators[0][0]
    } else {
      newComparator = searchOption.comparators[0][0]
    }

    clause.search_comparator = newComparator
    clause.custom_attribute_id = searchOption.custom_attribute_id || null

    if (searchOption.type === 'date' && newComparator === 'between') {
      clause.search_terms = [{
        id: null,
        value: '',
        values: { from: '', to: '' },
        _destroy: null
      }]
    } else {
      clause.search_terms = [this.newSearchTerm()]
    }

    let pillElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    if (pillElement) {
      const existingSearchTermsFields = pillElement.querySelectorAll(`input[type="hidden"][name*="[sta]"]`)
      existingSearchTermsFields.forEach(field => field.remove())
    }

    this.updateSearchClause(clauseIndex)

    pillElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    if (pillElement) {
      const pillController = this.application.getControllerForElementAndIdentifier(
        pillElement.querySelector('[data-controller*="select--filter-pill"]'),
        'select--filter-pill'
      )
      if (pillController) {
        // Close all segment dropdowns first
        pillController.closeAllSegmentDropdowns()

        // Open the values dropdown after a brief delay
        setTimeout(() => {
          if (pillController.hasValuesDropdownTarget && pillController.hasValuesSegmentTarget) {
            pillController.valuesDropdownTarget.classList.remove('d-none')
            pillController.positionDropdownBelowSegment(
              pillController.valuesDropdownTarget,
              pillController.valuesSegmentTarget
            )

            // Add scroll listener for this dropdown
            pillController.addScrollListenerForSegment(
              pillController.valuesDropdownTarget,
              pillController.valuesSegmentTarget
            )

            // Auto-open first searchable select (prioritize single for compatibility Make)
            setTimeout(() => {
              const searchableSelect = pillController.valuesDropdownTarget.querySelector('[data-controller*="searchable-select"]:not([data-controller*="multi-searchable-select"])')
              if (searchableSelect) {
                const controller = this.application.getControllerForElementAndIdentifier(searchableSelect, 'select--searchable-select')
                if (controller?.showDropdown) controller.showDropdown()
              } else {
                const multiSelect = pillController.valuesDropdownTarget.querySelector('[data-controller*="multi-searchable-select"]')
                if (multiSelect) {
                  const controller = this.application.getControllerForElementAndIdentifier(multiSelect, 'select--multi-searchable-select')
                  if (controller?.showDropdown) controller.showDropdown()
                } else {
                  const selectElement = pillController.valuesDropdownTarget.querySelector('select')
                  if (selectElement) {
                    selectElement.focus()
                    // Try to programmatically open the select using showPicker (modern browsers)
                    if (typeof selectElement.showPicker === 'function') {
                      try {
                        selectElement.showPicker()
                      } catch (e) {
                        // Fallback: simulate click event
                        selectElement.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }))
                      }
                    } else {
                      // Fallback for older browsers: simulate click
                      selectElement.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }))
                    }
                  } else {
                    // If no select, focus the first input field
                    const firstInput = pillController.valuesDropdownTarget.querySelector(
                      'input[type="text"], input[type="number"], input[type="date"]'
                    )
                    if (firstInput) {
                      firstInput.focus()
                    }
                  }
                }
              }
            }, 150)
          }
        }, 150)
      }
    }

    // Submit form
    this.debouncedSubmitForm()
  }

  handleMultiSelectApplied(event, expectedClauseState = null) {
    const { clauseIndex, selectedItems } = event.detail
    const clause = this.searchClauses[clauseIndex]

    if (!clause) return

    // For compatibility (multi-level-select), handle sub_model multi-select separately
    const searchOption = this.searchOptions[clause.search_key]
    if (searchOption?.type === 'multi-level-select') {
      this.handleCompatibilitySubModelApplied(clauseIndex, selectedItems)
      return
    }

    // If this is a retry, check if the clause's filter type or comparator changed
    // since the retry was scheduled. If so, discard the stale selection.
    if (expectedClauseState !== null) {
      const { searchKey, searchComparator } = expectedClauseState
      if (clause.search_key !== searchKey || clause.search_comparator !== searchComparator) {
        // Clause was modified, discard stale selection
        this.pendingMultiSelectUpdates = Math.max(0, (this.pendingMultiSelectUpdates || 0) - 1)
        return
      }
    }

    this.pendingMultiSelectUpdates = (this.pendingMultiSelectUpdates || 0) + 1

    if (this.isUpdatingClause) {
      // Capture current clause state to detect changes during retry delay
      const clauseStateAtRetry = {
        searchKey: clause.search_key,
        searchComparator: clause.search_comparator
      }
      // Keep the pending count incremented during retry
      setTimeout(() => {
        // Decrement since we're retrying (will be re-incremented on retry)
        this.pendingMultiSelectUpdates = Math.max(0, (this.pendingMultiSelectUpdates || 0) - 1)
        this.handleMultiSelectApplied(event, clauseStateAtRetry)
      }, 50)
      return
    }

    try {
      // Clear all existing search terms (mark as destroyed if they have IDs)
      clause.search_terms.forEach(term => {
        if (term.id) {
          term._destroy = 1
        }
      })

      // Remove terms that don't have IDs (not persisted yet)
      clause.search_terms = clause.search_terms.filter(term => term.id && term._destroy === 1)

      // Add new terms for each selected item
      selectedItems.forEach(([value, label]) => {
        const newTerm = this.newSearchTerm()
        newTerm.value = value
        newTerm.display_text = label
        clause.search_terms.push(newTerm)
      })

      // Update the pill label to show current selection
      this.updatePillSegmentLabels(clauseIndex)

      // Update hidden form fields for submission
      const pillElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
      if (pillElement) {
        // Remove old hidden fields for THIS specific clause only
        const existingHiddenFields = pillElement.querySelectorAll(`input[type="hidden"][name*="[sca][${clauseIndex}]"]`)
        existingHiddenFields.forEach(field => field.remove())

        // Add new clause-level hidden fields
        pillElement.insertAdjacentHTML('afterbegin', this.createHiddenFields(clause, clauseIndex))

        // Add search term hidden fields
        pillElement.insertAdjacentHTML('afterbegin', this.createSearchTermsHiddenFields(clause, clauseIndex))
      }
    } finally {
      this.pendingMultiSelectUpdates = Math.max(0, (this.pendingMultiSelectUpdates || 0) - 1)
    }

    // Don't close the values dropdown - keep it open for multiple selections
    // Users can click outside to close

    // Submit form
    this.debouncedSubmitForm()
  }

  handleCompatibilitySubModelApplied(clauseIndex, selectedItems) {
    const clause = this.searchClauses[clauseIndex]
    if (!clause) return

    const pillElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    const getVal = (key) => {
      const sel = pillElement?.querySelector(`[data-select-key="${key}_${clauseIndex}_0"] select`)
      return { v: sel?.value || '', l: sel?.selectedOptions?.[0]?.text || '' }
    }
    let make = getVal('make_id'), model = getVal('model_id'), year = getVal('year')
    const existingTerm = clause.search_terms.find(t => t._destroy !== 1)
    // Only fall back to existingTerm when BOTH make and model are empty (initial/reload state)
    // If make has a value but model is empty, it means dependent dropdowns were intentionally cleared
    if (!make.v && !model.v && existingTerm?.values) {
      make.v = make.v || existingTerm.values.make_id || ''
      model.v = model.v || existingTerm.values.model_id || ''
      year.v = year.v || existingTerm.values.year || ''
      if (existingTerm.selected_texts) {
        make.l = make.l || existingTerm.selected_texts.make_id || ''
        model.l = model.l || existingTerm.selected_texts.model_id || ''
        year.l = year.l || existingTerm.selected_texts.year || ''
      }
    }

    clause.search_terms.forEach(t => { if (t.id) t._destroy = 1 })
    clause.search_terms = clause.search_terms.filter(t => t.id && t._destroy === 1)

    const values = { make_id: make.v, model_id: model.v, year: year.v }
    const labels = { make_id: make.l, model_id: model.l, year: year.l }

    if (selectedItems.length > 0) {
      selectedItems.forEach(([id, label]) => {
        const t = this.newSearchTerm()
        t.values = { ...values, sub_model_id: id }
        t.selected_texts = { ...labels, sub_model_id: label }
        clause.search_terms.push(t)
      })
    } else {
      const t = this.newSearchTerm()
      t.values = { ...values, sub_model_id: '' }
      t.selected_texts = labels
      clause.search_terms.push(t)
    }

    this.updatePillSegmentLabels(clauseIndex)
    if (pillElement) {
      // Remove old hidden fields for THIS specific clause only (matching handleMultiSelectApplied pattern)
      const existingHiddenFields = pillElement.querySelectorAll(`input[type="hidden"][name*="[sca][${clauseIndex}]"]`)
      existingHiddenFields.forEach(field => field.remove())

      // Add clause-level hidden fields
      pillElement.insertAdjacentHTML('afterbegin', this.createHiddenFields(clause, clauseIndex))

      // Add search term hidden fields for compatibility
      pillElement.insertAdjacentHTML('afterbegin', this.createCompatibilityHiddenFields(clause, clauseIndex))
    }
    this.debouncedSubmitForm()
  }

  createCompatibilityHiddenFields(clause, clauseIndex) {
    const base = `ps[sca][${clauseIndex}][sta]`
    return clause.search_terms.map((t, i) => {
      if (t._destroy === 1) return `<input type="hidden" name="${base}[${i}][id]" value="${t.id || ''}"/><input type="hidden" name="${base}[${i}][_destroy]" value="1"/>`
      return `<input type="hidden" name="${base}[${i}][id]" value="${t.id || ''}"/><input type="hidden" name="${base}[${i}][_destroy]" value=""/><input type="hidden" name="${base}[${i}][values][make_id]" value="${t.values?.make_id || ''}"/><input type="hidden" name="${base}[${i}][values][model_id]" value="${t.values?.model_id || ''}"/><input type="hidden" name="${base}[${i}][values][year]" value="${t.values?.year || ''}"/><input type="hidden" name="${base}[${i}][values][sub_model_id]" value="${t.values?.sub_model_id || ''}"/>`
    }).join('')
  }

  // Create hidden fields for all search terms (for form submission)
  createSearchTermsHiddenFields(clause, clauseIndex) {
    return clause.search_terms.map((term, termIndex) => {
      return `
        <input type="hidden" name="ps[sca][${clauseIndex}][sta][${termIndex}][id]" value="${term.id || ''}" />
        <input type="hidden" name="ps[sca][${clauseIndex}][sta][${termIndex}][value]" value="${term.value || ''}" />
        <input type="hidden" name="ps[sca][${clauseIndex}][sta][${termIndex}][_destroy]" value="${term._destroy || 0}" />
      `
    }).join('')
  }

  // Override to fix event listener binding issue in BackOffice
  initializeSearchableSelectListeners() {
    // Find all hidden select elements within searchable select components (for search term values)
    // Use more specific selector for BackOffice namespace
    const valueSearchableSelects = this.element.querySelectorAll('[data-controller*="select--searchable-select"] select[data-target*="searchable-select.hiddenSelect"]:not([name*="[sk]"])')

    valueSearchableSelects.forEach(hiddenSelect => {
      // Remove any existing listeners to avoid duplicates (using stored bound reference)
      hiddenSelect.removeEventListener('change', this.boundHandleSearchableSelectChange)

      // Add new listener (using stored bound reference)
      hiddenSelect.addEventListener('change', this.boundHandleSearchableSelectChange)
    })

    // Find filter type searchable selects (in pill segment dropdowns)
    const filterTypeSelects = this.element.querySelectorAll('[data-controller*="select--searchable-select"] select[name*="[sk]"]')

    filterTypeSelects.forEach(hiddenSelect => {
      // Remove any existing listeners to avoid duplicates
      hiddenSelect.removeEventListener('change', this.boundHandleFilterTypeChange)

      // Add new listener for filter type changes
      hiddenSelect.addEventListener('change', this.boundHandleFilterTypeChange)
    })
  }

  // Handle searchable select changes and update pill labels
  handleSearchableSelectChange(event) {
    const hiddenSelect = event.target
    const clauseIndex = this.getClauseIndexFromElement(hiddenSelect)

    if (this.isResettingDependents || clauseIndex === null) {
      return
    }

    // Check if this is a multi-level select (compatibility) - keep dropdown open
    const clause = this.searchClauses[clauseIndex]
    if (clause) {
      const searchOption = this.searchOptions[clause.search_key]
      const isMultiLevelSelect = searchOption && searchOption.type === 'multi-level-select'

      // For multi-level selects (compatibility), keep dropdown open
      this.updateClauseAndPill(clauseIndex, !isMultiLevelSelect)
    } else {
      this.updateClauseAndPill(clauseIndex, true)
    }
  }

  checkAndSubmitForm(clauseIndex) {
    if (!this.isInsideModal()) {
      this.debouncedSubmitForm()
    }
  }

  updatePillLabel(clauseIndex) {
    this.updatePillSegmentLabels(clauseIndex)
  }

  handleSelectChange(event) {
    const clauseIndex = parseInt(event.target.dataset.clauseIndex)
    this.updateClauseAndPill(clauseIndex, true) // Close dropdown for select changes
  }

  handleInputChange(event) {
    const clauseIndex = parseInt(event.target.dataset.clauseIndex)

    if (this.inputTimer) {
      clearTimeout(this.inputTimer)
    }

    this.inputTimer = setTimeout(() => {
      this.updateClauseAndPill(clauseIndex, false) // Keep dropdown open for input changes
    }, 500)
  }

  // Helper method to update clause data, pill labels, optionally close dropdowns and submit
  updateClauseAndPill(clauseIndex, shouldCloseDropdown = true) {
    // Sync clause data from DOM
    this.syncClauseDataFromDOM(clauseIndex)

    // Update pill display
    this.updatePillSegmentLabels(clauseIndex)

    // Close dropdown only if specified (don't close for text/number/date inputs)
    if (shouldCloseDropdown) {
      const pillElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
      if (pillElement) {
        const pillController = this.application.getControllerForElementAndIdentifier(
          pillElement.querySelector('[data-controller*="select--filter-pill"]'),
          'select--filter-pill'
        )
        if (pillController) {
          pillController.closeAllSegmentDropdowns()
        }
      }
    }

    // Submit form
    this.checkAndSubmitForm(clauseIndex)
  }

  // Override parent's syncClauseDataFromDOM to fix bug with hidden input fields
  syncClauseDataFromDOM(clauseIndex) {
    const clause = this.searchClauses[clauseIndex]
    if (!clause || clause._destroy === 1) return

    const existingElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    if (!existingElement) return

    // Sync search_key
    const searchKeySelect = existingElement.querySelector(`select[name*="[search_key]"]`)
    if (searchKeySelect) {
      clause.search_key = searchKeySelect.value
    }

    // Sync custom_attribute_id from hidden field
    const customAttributeIdInput = existingElement.querySelector(`input[name*="[custom_attribute_id]"]`)
    if (customAttributeIdInput) {
      clause.custom_attribute_id = customAttributeIdInput.value || null
    }

    // Sync search_comparator
    const comparatorSelect = existingElement.querySelector(`select[name*="[search_comparator]"]`)
    if (comparatorSelect) {
      clause.search_comparator = comparatorSelect.value
    }

    // Sync search_terms
    clause.search_terms.forEach((term, termIndex) => {
      if (term._destroy === 1) return

      // Initialize selected_texts if not exists
      if (!term.selected_texts) {
        term.selected_texts = {}
      }

      // Sync single value (can be input or select)
      // FIX: Exclude hidden inputs to avoid reading stale values from form submission fields
      const valueInput = existingElement.querySelector(`input[name*="[search_terms_attributes][${termIndex}][value]"]:not([type="hidden"])`)
      const valueSelect = existingElement.querySelector(`select[name*="[search_terms_attributes][${termIndex}][value]"]`)

      if (valueInput) {
        term.value = valueInput.value
      } else if (valueSelect) {
        term.value = valueSelect.value
        // Get display text from select option
        if (valueSelect.selectedOptions.length > 0) {
          term.selected_texts['value'] = valueSelect.selectedOptions[0].text
        }
      }

      // Sync multi-level values (compatibility) - can be inputs or selects
      // FIX: Exclude hidden inputs to avoid reading stale values
      const valuesInputs = existingElement.querySelectorAll(`input[name*="[search_terms_attributes][${termIndex}][values]"]:not([type="hidden"])`)
      const valuesSelects = existingElement.querySelectorAll(`select[name*="[search_terms_attributes][${termIndex}][values]"]`)

      // Sync from input elements (text, date, number fields)
      valuesInputs.forEach(input => {
        const match = input.name.match(/\[values\]\[(\w+)\]/)
        if (match) {
          const key = match[1]
          if (!term.values) term.values = {}
          term.values[key] = input.value
        }
      })

      // Sync from select elements (searchable selects)
      valuesSelects.forEach(selectElement => {
        const match = selectElement.name.match(/\[values\]\[(\w+)\]/)
        if (match) {
          const key = match[1]
          if (!term.values) term.values = {}
          term.values[key] = selectElement.value

          // Get the display text from the selected option
          if (selectElement.selectedOptions.length > 0) {
            term.selected_texts[key] = selectElement.selectedOptions[0].text
          }
        }
      })

      // Sync selected_texts from hidden inputs (if they exist - fallback)
      const selectedTextsInputs = existingElement.querySelectorAll(`input[name*="[search_terms_attributes][${termIndex}][selected_texts]"]`)
      selectedTextsInputs.forEach(input => {
        const match = input.name.match(/\[selected_texts\]\[(\w+)\]/)
        if (match) {
          term.selected_texts[match[1]] = input.value
        }
      })
    })
  }

  // Override to add logging
  debouncedSubmitForm() {
    // Clear any existing submit timer
    if (this.submitTimer) {
      clearTimeout(this.submitTimer)
    }

    // Set a new timer to submit after a short delay
    this.submitTimer = setTimeout(() => {
      // Double-check that we're not still resetting dependents
      if (this.isResettingDependents) {
        this.debouncedSubmitForm() // Retry after another delay
        return
      }
      // Wait for pending multi-select updates to complete
      if (this.pendingMultiSelectUpdates > 0) {
        this.debouncedSubmitForm() // Retry after another delay
        return
      }
      this.submitForm()
    }, 150) // 150ms delay to allow dependent dropdown changes to complete
  }

  // Override POS submit to target this form/turbo-frame in BackOffice
  submitForm() {
    // Update pill labels before submission
    this.searchClauses.forEach((clause, index) => {
      if (clause._destroy !== 1) {
        this.updatePillLabel(index)
      }
    })

    // Submit the current form directly (no POS search-btn)
    if (this.element && typeof this.element.requestSubmit === 'function') {
      this.element.requestSubmit()
    } else if (this.element) {
      this.element.submit()
    }
  }

  setupSearchFieldListener() {
    const searchFieldSelect = this.element.querySelector('select[name="variant[search_field]"]')
    if (searchFieldSelect) {
      searchFieldSelect.addEventListener('change', (e) => {
        const selectedKey = e.target.value
        if (selectedKey) {
          this.addSearchClauseFromKey(selectedKey)
          searchFieldSelect.value = ''
        }
      })

      searchFieldSelect.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          const selectedKey = searchFieldSelect.value
          if (selectedKey) {
            this.addSearchClauseFromKey(selectedKey)
            searchFieldSelect.value = ''
          }
        }
      })
    }
  }

  initializeSearchClause(clause = {}) {
    const searchKey = clause.search_key || Object.keys(this.searchOptions)[0]
    const searchOption = this.searchOptions[searchKey]

    let comparator = clause.search_comparator
    if (!comparator) {
      if (searchOption?.type === 'date' && searchOption?.comparators) {
        const hasBetween = searchOption.comparators.some(c => c[0] === 'between')
        comparator = hasBetween ? 'between' : searchOption.comparators[0][0]
      } else {
        comparator = searchOption?.comparators[0][0] || 'is'
      }
    }

    let searchTerms = clause.search_terms || clause.search_terms_attributes
    if (!searchTerms || searchTerms.length === 0) {
      if (searchOption?.type === 'date' && comparator === 'between') {
        searchTerms = [{
          id: null,
          value: '',
          values: { from: '', to: '' },
          _destroy: null
        }]
      } else {
        searchTerms = [this.newSearchTerm()]
      }
    }

    return {
      id: clause.id || null,
      search_key: searchKey,
      custom_attribute_id: clause.custom_attribute_id || searchOption?.custom_attribute_id || null,
      search_comparator: comparator,
      search_terms: searchTerms,
      _destroy: clause._destroy || null
    }
  }

  addSearchClauseFromKey(searchKey) {
    if (!this.searchOptions[searchKey]) return

    const newClause = this.initializeSearchClause({ search_key: searchKey })
    this.searchClauses.push(newClause)

    const clauseElement = this.createSearchClausePill(newClause, this.searchClauses.length - 1)
    this.searchClauseContainerTarget.appendChild(clauseElement)

    // Scroll to show the newly added pill
    setTimeout(() => {
      this.searchClauseContainerTarget.scrollTo({
        left: this.searchClauseContainerTarget.scrollWidth,
        behavior: 'smooth'
      })
    }, 50)

    this.initializeSearchableSelectListeners()
    this.updateResetButtonVisibility()
    this.clauseIndex++
  }

  createSearchClausePill(clause, index) {
    const pillDiv = document.createElement('div')
    pillDiv.className = 'd-inline-block'
    pillDiv.dataset.clauseIndex = index

    const segments = this.createPillSegments(clause, index)

    // Hide remove button for locked date filter if it's in position 0
    const removeButtonDisplay = (this.lockedDateKey && clause.search_key === this.lockedDateKey && index === 0) ? 'none' : 'flex'

    const pillHtml = this.renderTemplate('filter-pill-template', {
      clauseIndex: index,
      filterTypeLabel: segments.filterTypeLabel,
      comparatorLabel: segments.comparatorLabel,
      valuesLabel: segments.valuesLabel,
      filterTypeDropdownContent: segments.filterTypeDropdownContent,
      comparatorDropdownContent: segments.comparatorDropdownContent,
      valuesDropdownContent: segments.valuesDropdownContent,
      pillLabel: segments.pillLabel,
      pillEditorContent: segments.pillEditorContent,
      removeButtonDisplay: removeButtonDisplay
    })

    // Include search term hidden fields for multi-select types on initial render
    // to ensure form submission works even if user doesn't interact with multi-selects
    const searchTermsHiddenFields = this.shouldIncludeSearchTermsHiddenFields(clause)
      ? this.createSearchTermsHiddenFields(clause, index)
      : ''

    pillDiv.innerHTML = `
      ${this.createHiddenFields(clause, index)}
      ${searchTermsHiddenFields}
      ${pillHtml}
    `

    // After inserting HTML, manually connect Stimulus controllers for nested components
    setTimeout(() => {
      // Connect searchable select controller in filter type dropdown
      const filterTypeDropdown = pillDiv.querySelector('[data-target*="filter-pill.filterTypeDropdown"]')
      if (filterTypeDropdown) {
        const searchableSelect = filterTypeDropdown.querySelector('[data-controller*="searchable-select"]')
        if (searchableSelect && this.application) {
          // Stimulus will auto-connect controllers in newly added DOM
        }
      }

      // Connect multi-searchable select controller in values dropdown
      const valuesDropdown = pillDiv.querySelector('[data-target*="filter-pill.valuesDropdown"]')
      if (valuesDropdown) {
        const multiSelect = valuesDropdown.querySelector('[data-controller*="multi-searchable-select"]')
        if (multiSelect && this.application) {
          // Stimulus will auto-connect controllers in newly added DOM
        }
      }
    }, 0)

    return pillDiv
  }

  // Helper to determine if search term hidden fields should be included
  // Returns true for multi-select types (searchable selects and simple selects using multi-select component)
  shouldIncludeSearchTermsHiddenFields(clause) {
    const searchOption = this.searchOptions[clause.search_key]
    if (!searchOption) return false

    // Check if clause has any active search terms with values
    const hasActiveTerms = clause.search_terms.some(term =>
      term._destroy !== 1 && (term.value !== undefined && term.value !== null && term.value !== '')
    )

    if (!hasActiveTerms) return false

    // Check if it's a multi-select type (single searchable select or simple select)
    const isSingleSearchableSelect = searchOption.endpoints &&
      searchOption.endpoints[clause.search_comparator] &&
      searchOption.type !== 'multi-level-select'

    const isSimpleSelect = searchOption.type === 'select' && clause.search_comparator !== 'is_blank'

    return isSingleSearchableSelect || isSimpleSelect
  }

  // Create pill segments (replaces createPillLabel for segmented UI)
  createPillSegments(clause, index) {
    const searchOption = this.searchOptions[clause.search_key]
    const comparator = searchOption.comparators.find(c => c[0] === clause.search_comparator)

    return {
      filterTypeLabel: searchOption.title,
      comparatorLabel: comparator ? comparator[1] : clause.search_comparator,
      valuesLabel: this.formatValuesLabel(clause),
      filterTypeDropdownContent: this.createFilterTypeDropdownContent(clause, index),
      comparatorDropdownContent: this.createComparatorDropdownContent(clause, index),
      valuesDropdownContent: this.createValuesDropdownContent(clause, index)
    }
  }

  // Format values for display in the pill
  formatValuesLabel(clause) {
    const activeTerms = clause.search_terms.filter(term => term._destroy !== 1)

    if (activeTerms.length === 0 || clause.search_comparator === 'is_blank') {
      return '<span class="text-muted font-italic" style="font-size: 0.9em;">click to add</span>'
    }

    const searchOption = this.searchOptions[clause.search_key]

    const labels = activeTerms.map((term, termIndex) => {
      if (term.values && Object.keys(term.values).length > 0) {
        // Handle date 'between' comparator with all_time option
        if (clause.search_comparator === 'between') {
          if (term.values.all_time === 'true' || term.values.all_time === true) {
            return 'All time'
          }
          if (term.values.from || term.values.to) {
            const fromDate = term.values.from || '...'
            const toDate = term.values.to || '...'
            return `${fromDate} and ${toDate}`
          }
        }

        // Multi-level select (compatibility) - use selected_texts for display names
        const valueLabels = []
        Object.entries(term.values).forEach(([key, value]) => {
          // Skip all_time key as it's handled above
          if (key === 'all_time') return

          if (value) {
            // First try selected_texts (human-readable labels)
            if (term.selected_texts && term.selected_texts[key]) {
              valueLabels.push(term.selected_texts[key])
            }
            // Fallback to initial_options
            else if (term.initial_options && term.initial_options[key]) {
              const option = term.initial_options[key].find(opt => opt.id === value || opt.year === value)
              valueLabels.push(option ? (option.name || option.year || value) : value)
            }
            // Last resort: use the value itself
            else {
              valueLabels.push(value)
            }
          }
        })
        return valueLabels.join(' > ')
      } else {
        // Single value - check multiple sources for label
        // 1. Try display_text (set by multi-select)
        if (term.display_text) {
          return term.display_text
        }
        // 2. Try selected_texts['value'] (set by syncClauseDataFromDOM for simple selects)
        if (term.selected_texts && term.selected_texts['value']) {
          return term.selected_texts['value']
        }
        // 3. For simple select type, look up the label from options
        if (searchOption && searchOption.type === 'select' && searchOption.options && term.value !== undefined && term.value !== null && term.value !== '') {
          const termValue = String(term.value)
          const option = searchOption.options.find(([val, label]) => String(val) === termValue)
          if (option) {
            return option[1]
          }
        }
        // 3. Try initial_options (for searchable selects)
        if (term.initial_options && term.initial_options.length > 0) {
          const option = term.initial_options.find(opt => opt.id == term.value)
          if (option && option.name) {
            return option.name
          }
        }
        // 4. Fallback to raw value
        return term.value
      }
    }).filter(label => label)

    if (labels.length === 0) {
      return '<span class="text-muted font-italic" style="font-size: 0.9em;">click to add</span>'
    } else if (labels.length === 1) {
      return labels[0]
    } else {
      // For 2+ selections: show "FirstLabel, +N more"
      return `${labels[0]}, <span class="text-muted">+${labels.length - 1} more</span>`
    }
  }

  // Create filter type dropdown content (uses searchable select component)
  createFilterTypeDropdownContent(clause, index) {
    // Prepare options for searchable select
    const entries = Object.entries(this.searchOptions)
    const standardOptions = entries
      .filter(([_key, option]) => !option.custom_attribute_id)
      .map(([key, option]) => ({ id: key, name: option.title || key, group: 'Standard Filters' }))
    const customAttributeOptions = entries
      .filter(([_key, option]) => option.custom_attribute_id)
      .map(([key, option]) => ({ id: key, name: option.title || key, group: 'Custom Attributes' }))
    const options = standardOptions.concat(customAttributeOptions)

    const selectId = `filter_type_select_${index}`
    const fieldName = `ps[sca][${index}][sk]`

    const searchOption = this.searchOptions[clause.search_key]

    // Render searchable select template
    let html = this.renderTemplate('searchable-select-template', {
      fieldName: fieldName,
      endpoint: '',
      valueField: 'id',
      labelField: 'name',
      placeholder: 'Search filters...',
      fieldId: selectId,
      selectedValue: clause.search_key || '',
      selectedLabel: searchOption.title || 'Select filter...',
      dependentParam: '',
      dependentParamName: '',
      otherDependentParam: '',
      otherDependentParamName: '',
      hideSearchStyle: ''
    })

    // Add options data attribute (event handler will be added via initializeSearchableSelectListeners)
    html = html.replace(
      'data-controller="select--searchable-select"',
      `data-controller="select--searchable-select" data-options='${JSON.stringify(options).replace(/'/g, '&apos;')}'`
    )

    return html
  }

  // Create comparator dropdown content
  createComparatorDropdownContent(clause, index) {
    const searchOption = this.searchOptions[clause.search_key]
    const options = searchOption.comparators.map(([value, label]) => ({ id: value, name: label }))

    const selectId = `comparator_select_${index}`
    const fieldName = `ps[sca][${index}][sc]`

    const comparator = searchOption.comparators.find(c => c[0] === clause.search_comparator)
    const selectedLabel = comparator ? comparator[1] : 'Select comparator...'

    let html = this.renderTemplate('searchable-select-template', {
      fieldName: fieldName,
      endpoint: '',
      valueField: 'id',
      labelField: 'name',
      placeholder: 'Comparator',
      fieldId: selectId,
      selectedValue: clause.search_comparator || '',
      selectedLabel: selectedLabel,
      dependentParam: '',
      dependentParamName: '',
      otherDependentParam: '',
      otherDependentParamName: '',
      hideSearchStyle: 'display: none;'
    })

    html = html.replace(
      'data-controller="select--searchable-select"',
      `data-controller="select--searchable-select" data-options='${JSON.stringify(options).replace(/'/g, '&apos;')}' data-action="change->select--advanced-filter#handleComparatorChangeAndClose"`
    )

    return html
  }

  // Create values dropdown content
  createValuesDropdownContent(clause, index) {
    const searchOption = this.searchOptions[clause.search_key]

    // For compatibility (multi-level-select), render single row with sub_model multi-select
    if (searchOption.type === 'multi-level-select' && clause.search_comparator !== 'is_blank') {
      const term = clause.search_terms.find(t => t._destroy !== 1) || { values: {}, selected_texts: {}, initial_options: {} }
      return `<div class="p-3"><label class="d-block small font-weight-medium text-muted mb-2">Value(s)</label><div class="d-flex flex-column" style="gap: 0.75rem;">${this.createMultiLevelSelectElement(clause, index, term, 0)}</div></div>`
    }

    // Check if it's a single searchable select (has endpoints, not multi-level)
    const isSingleSearchableSelect = searchOption.endpoints &&
      searchOption.endpoints[clause.search_comparator] &&
      searchOption.type !== 'multi-level-select'

    // Check if it's a simple select (type='select')
    const isSimpleSelect = searchOption.type === 'select' && clause.search_comparator !== 'is_blank'

    if (isSingleSearchableSelect) {
      // Use multi-select searchable component for single searchable selects
      return this.createMultiSelectSearchableDropdown(clause, index, searchOption)
    } else if (isSimpleSelect) {
      // Use multi-select searchable component for simple selects
      return this.createMultiSelectSearchableForSimpleSelectDropdown(clause, index, searchOption)
    } else {
      // For multi-level selects, text/number/date inputs, use existing search terms UI
      return this.createSearchTermsDropdown(clause, index, searchOption)
    }
  }

  createMultiSelectSearchableForSimpleSelectDropdown(clause, index, searchOption) {
    const options = searchOption.options.map(([value, label]) => ({ id: value, name: label }))

    const fieldName = `ps[sca][${index}][sta]`
    const fieldId = `multi_searchable_simple_${index}`

    const activeTerms = clause.search_terms.filter(t => t._destroy !== 1)
    const selectedValues = []
    const selectedLabels = []

    activeTerms.forEach(t => {
      if (t.value !== undefined && t.value !== null && t.value !== '') {
        const termValue = String(t.value)
        selectedValues.push(termValue)
        const option = searchOption.options.find(([val, label]) => String(val) === termValue)
        selectedLabels.push(option ? option[1] : termValue)
      }
    })

    const multiSelectHtml = this.renderMultiSearchableSelectFromTemplate({
      fieldName: fieldName,
      fieldId: fieldId,
      endpoint: '',
      valueField: 'id',
      labelField: 'name',
      placeholder: 'Type to search...',
      clauseIndex: index,
      selectedValues: selectedValues,
      selectedLabels: selectedLabels,
      options: options
    })

    // Return directly without wrapper for single search term
    return multiSelectHtml
  }

  // Create multi-select searchable dropdown (for single searchable selects)
  createMultiSelectSearchableDropdown(clause, index, searchOption) {
    const endpoint = searchOption.endpoints && searchOption.endpoints[clause.search_comparator]

    if (!endpoint) {
      console.error('Multi-select: No endpoint found for', clause.search_key, clause.search_comparator)
      return '<div class="p-3"><p class="text-danger">No endpoint configured</p></div>'
    }

    const fieldName = `ps[sca][${index}][sta]`
    const fieldId = `multi_searchable_${index}`

    // Extract existing selected values and labels from search terms
    const activeTerms = clause.search_terms.filter(term => term._destroy !== 1)
    const selectedValues = []
    const selectedLabels = []

    activeTerms.forEach(term => {
      if (term.value) {
        selectedValues.push(term.value)
        // Try display_text first, then initial_options, then value as fallback
        let label = term.value
        if (term.display_text) {
          label = term.display_text
        } else if (term.initial_options && term.initial_options.length > 0) {
          const option = term.initial_options.find(opt => opt.id == term.value)
          if (option) {
            label = option.name || option.text || term.value
          }
        }
        selectedLabels.push(label)
      }
    })

    // Use the template-based approach (same as searchable select)
    const multiSelectHtml = this.renderMultiSearchableSelectFromTemplate({
      fieldName: fieldName,
      fieldId: fieldId,
      endpoint: endpoint.url,
      valueField: endpoint.value_field || 'id',
      labelField: endpoint.label_field || 'name',
      placeholder: 'Type to search...',
      clauseIndex: index,
      selectedValues: selectedValues,
      selectedLabels: selectedLabels
    })

    // Return directly without wrapper for single search term
    return multiSelectHtml
  }

  // Render multi-searchable select using HTML template (same approach as searchable select)
  renderMultiSearchableSelectFromTemplate(config) {
    const template = document.getElementById('multi-searchable-select-template')
    if (!template) {
      console.error('Multi-searchable select template not found')
      return '<div class="p-3"><p class="text-danger">Template not found</p></div>'
    }

    let html = template.innerHTML

    // Replace all placeholders with actual values (same as searchable select)
    html = html.replace(/\{\{fieldName\}\}/g, config.fieldName)
    html = html.replace(/\{\{fieldId\}\}/g, config.fieldId)
    html = html.replace(/\{\{endpoint\}\}/g, config.endpoint)
    html = html.replace(/\{\{valueField\}\}/g, config.valueField)
    html = html.replace(/\{\{labelField\}\}/g, config.labelField)
    html = html.replace(/\{\{placeholder\}\}/g, config.placeholder)

    // Replace clause_index (hardcoded to 0 in template, need to update dynamically)
    html = html.replace(/data-select--multi-searchable-select-clause-index-value="0"/g,
      `data-select--multi-searchable-select-clause-index-value="${config.clauseIndex}"`)

    // Replace the hidden inputs container with actual selected values
    const selectedValues = config.selectedValues || []
    const selectedLabels = config.selectedLabels || []

    if (selectedValues.length > 0) {
      // Generate hidden inputs HTML (without name attribute - only for tracking)
      const hiddenInputsHtml = selectedValues.map((value, index) => {
        const label = selectedLabels[index] || value
        return `<input type="hidden" value="${value}" id="${config.fieldId}_${index}" data-label="${label}" />`
      }).join('\n      ')

      // Replace the empty hidden inputs container
      html = html.replace(
        /<div data-target="select--multi-searchable-select\.hiddenInputsContainer"[^>]*>\s*<\/div>/,
        `<div data-target="select--multi-searchable-select.hiddenInputsContainer" data-field-name="${config.fieldName}">\n      ${hiddenInputsHtml}\n    </div>`
      )

      // Update display label to show selected count
      const labelText = selectedValues.length === 1
        ? selectedLabels[0]
        : `${selectedLabels[0]}, +${selectedValues.length - 1} more`

      html = html.replace(
        /<span data-target="select--multi-searchable-select\.labelText"[^>]*>([^<]*)<\/span>/,
        `<span data-target="select--multi-searchable-select.labelText">${labelText}</span>`
      )

      // Update selected count in footer
      html = html.replace(
        /<span data-target="select--multi-searchable-select\.selectedCount">0<\/span>/,
        `<span data-target="select--multi-searchable-select.selectedCount">${selectedValues.length}</span>`
      )
    }

    if (config.options) {
      html = html.replace(
        'data-controller="select--multi-searchable-select"',
        `data-controller="select--multi-searchable-select" data-options='${JSON.stringify(config.options).replace(/'/g, '&apos;')}'`
      )
    }

    // Add dependent params for sub_model filtering
    if (config.dependentParam || config.otherDependentParam) {
      const attrs = []
      if (config.dependentParam) attrs.push(`data-dependent-param="${config.dependentParam}" data-dependent-param-name="${config.dependentParamName}"`)
      if (config.otherDependentParam) attrs.push(`data-other-dependent-param="${config.otherDependentParam}" data-other-dependent-param-name="${config.otherDependentParamName}"`)
      html = html.replace('data-controller="select--multi-searchable-select"', `data-controller="select--multi-searchable-select" ${attrs.join(' ')}`)
    }

    return html
  }

  // Override to use multi-searchable select for sub_model_id
  createMultiLevelSelectElement(clause, clauseIndex, term, termIndex) {
    const searchOption = this.searchOptions[clause.search_key]
    const selects = searchOption.selects || []
    const selectElements = selects.map((selectKey) => {
      const endpoint = searchOption.endpoints && searchOption.endpoints[clause.search_comparator] && searchOption.endpoints[clause.search_comparator][selectKey]
      const fieldName = `ps[sca][${clauseIndex}][sta][${termIndex}][values][${selectKey}]`
      const fieldId = `search_clause_${clauseIndex}_term_${termIndex}_${selectKey}`
      const currentValue = term.values?.[selectKey] || ''
      const placeholder = this.getPlaceholderForSelectKey(selectKey)
      const initialOptions = term.initial_options?.[selectKey] || []
      const dep = (id) => id ? `${id}_${clauseIndex}_${termIndex}` : ''

      if (selectKey === 'sub_model_id') {
        const activeTerms = clause.search_terms.filter(t => t._destroy !== 1 && t.values?.sub_model_id)
        const getLabel = (t) => {
          if (t.selected_texts?.sub_model_id) return t.selected_texts.sub_model_id
          const opts = t.initial_options?.sub_model_id || []
          const found = opts.find(o => o.id === t.values.sub_model_id)
          return found?.name || t.values.sub_model_id
        }
        return this.renderTemplate('select-wrapper-template', {
          selectKey: `${selectKey}_${clauseIndex}_${termIndex}`,
          selectHtml: this.renderMultiSearchableSelectFromTemplate({
            fieldName, fieldId, placeholder, clauseIndex,
            endpoint: endpoint.url,
            valueField: endpoint.value_field || 'id',
            labelField: endpoint.label_field || 'name',
            selectedValues: activeTerms.map(t => t.values.sub_model_id),
            selectedLabels: activeTerms.map(t => getLabel(t)),
            dependentParam: dep(endpoint.dependent_param_id),
            dependentParamName: endpoint.dependent_param_id || '',
            otherDependentParam: dep(endpoint.other_dependent_param_id),
            otherDependentParamName: endpoint.other_dependent_param_id || ''
          })
        })
      }

      const selectedText = initialOptions.length > 0 ? (initialOptions[0].year == currentValue ? currentValue : initialOptions.find(o => o.id === currentValue)?.name || '') : ''
      return this.renderTemplate('select-wrapper-template', {
        selectKey: `${selectKey}_${clauseIndex}_${termIndex}`,
        selectHtml: this.renderSearchableSelectFromTemplate({
          fieldName, fieldId, placeholder, clauseIndex, searchKey: selectKey,
          endpoint: endpoint.url, valueField: endpoint.value_field || 'id', labelField: endpoint.label_field || 'name',
          selectedValue: currentValue, selectedText,
          dependentParam: dep(endpoint.dependent_param_id), dependentParamName: endpoint.dependent_param_id || '',
          otherDependentParam: dep(endpoint.other_dependent_param_id), otherDependentParamName: endpoint.other_dependent_param_id || '',
          isMultiSelect: true, initialOptions: initialOptions.length > 0 ? JSON.stringify(initialOptions) : ''
        })
      })
    }).join('')
    return this.renderTemplate('multi-level-select-container-template', { selectElements })
  }

  // Create search terms dropdown (for multi-level selects and text inputs)
  createSearchTermsDropdown(clause, index, searchOption) {
    const termsHtml = clause.search_terms
      .map((term, termIndex) => this.createSearchTermElement(clause, index, term, termIndex))
      .join('')

    return `
      <div class="p-3">
        <label class="d-block small font-weight-medium text-muted mb-2">Value(s)</label>
        <div class="d-flex flex-column" style="gap: 0.75rem;" data-search-terms-container="${index}">
          ${termsHtml}
        </div>
      </div>
    `
  }

  // Use Bootstrap-styled OR button with correct controller namespace
  createOrButton(clauseIndex) {
    const orBtn = document.createElement('button')
    orBtn.type = 'button'
    orBtn.className = 'btn btn-sm btn-primary'
    orBtn.dataset.action = 'click->select--advanced-filter#addSearchTerm'
    orBtn.dataset.clauseIndex = String(clauseIndex)
    orBtn.textContent = '+OR'
    return orBtn
  }

  // Override to only show OR button for single searchable selects
  createSearchTermButtons(clause, clauseIndex, termIndex) {
    const activeTerms = clause.search_terms.filter(term => term._destroy !== 1)
    const isLastTerm = termIndex === activeTerms.length - 1
    const canRemove = activeTerms.length > 1

    const searchOption = this.searchOptions[clause.search_key]

    // Only show OR button if:
    // 1. It's the last term
    // 2. The search option has endpoints for this comparator (is a searchable select)
    // 3. It's NOT a multi-level select (compatibility has multiple dropdowns)
    const isSingleSearchableSelect = searchOption.endpoints &&
      searchOption.endpoints[clause.search_comparator] &&
      searchOption.type !== 'multi-level-select'

    // Use templates for button rendering
    const removeButton = canRemove ? this.renderTemplate('remove-term-button-template', {
      clauseIndex: clauseIndex,
      termIndex: termIndex
    }) : ''

    const orButton = (isLastTerm && isSingleSearchableSelect) ? this.renderTemplate('add-or-button-template', {
      clauseIndex: clauseIndex
    }) : ''

    return this.renderTemplate('term-buttons-template', {
      removeButton: removeButton,
      orButton: orButton
    })
  }

  // Override to clear search_scope_id and handle backoffice-specific reset
  resetFilters(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    // Keep the locked date filter if it's in position 0, remove all other search clauses
    const dateClause = this.lockedDateKey && this.searchClauses[0] && this.searchClauses[0].search_key === this.lockedDateKey && this.searchClauses[0]._destroy !== 1
      ? this.searchClauses[0]
      : null
    if (dateClause) {
      // Reset date filter to today's date
      const today = this.calculateDateRange('today')
      dateClause.search_terms = [{
        id: null,
        value: '',
        values: { from: today.fromDate, to: today.toDate },
        _destroy: null
      }]
      this.searchClauses = [dateClause]
    } else {
      this.searchClauses = []
    }
    this.renderSearchClauses()

    // Update reset button visibility after clearing filters
    this.updateResetButtonVisibility()

    // Clear search_scope_id to prevent reloading saved search
    const searchScopeIdInput = this.element.querySelector('input[name="search_scope_id"]')
    if (searchScopeIdInput) {
      searchScopeIdInput.value = ''
    }

    // Clear full_search hidden field if it exists
    const fullSearchInput = this.element.querySelector('input[name="ps[full_search]"]')
    if (fullSearchInput) {
      fullSearchInput.value = ''
    }

    // Submit the form to reset
    this.submitForm()
  }

  removeSearchClause(event) {
    // Find the button element (in case SVG or path was clicked)
    const button = event.target.closest('button[data-clause-index]')
    if (!button) {
      console.error('Could not find button with clause index')
      return
    }

    const clauseIndex = parseInt(button.dataset.clauseIndex)

    // Prevent removal of locked date filter if it's in position 0
    const clause = this.searchClauses[clauseIndex]
    if (clause && this.lockedDateKey && clause.search_key === this.lockedDateKey && clauseIndex === 0) {
      console.warn('Locked date filter in position 0 cannot be removed')
      return
    }

    const searchScopeIdInput = this.element.querySelector('input[name="search_scope_id"]')
    if (searchScopeIdInput) {
      searchScopeIdInput.value = ''
    }

    super.removeSearchClause(event)

    // Get all remaining pill elements in DOM (excluding destroyed ones that have been removed)
    // Use direct children to avoid nested elements with data-clause-index
    const remainingPills = Array.from(this.searchClauseContainerTarget.children).filter(el =>
      el.hasAttribute('data-clause-index')
    )

    // Track the actual DOM position separately from array index
    let domPosition = 0
    this.searchClauses.forEach((clause, newIndex) => {
      if (clause._destroy !== 1) {
        // Get the pill element at this DOM position
        const clauseElement = remainingPills[domPosition]
        domPosition++

        if (clauseElement && parseInt(clauseElement.dataset.clauseIndex) !== newIndex) {
          const oldIndex = clauseElement.dataset.clauseIndex

          // Update the outer div's data-clause-index
          clauseElement.dataset.clauseIndex = newIndex

          // Update ALL child elements with data-clause-index (especially the remove button)
          clauseElement.querySelectorAll('[data-clause-index]').forEach(childElement => {
            if (childElement.dataset.clauseIndex === oldIndex) {
              childElement.dataset.clauseIndex = newIndex
            }
          })

          // Update hidden input field names
          clauseElement.querySelectorAll('input[type="hidden"]').forEach(input => {
            if (input.name) {
              input.name = input.name.replace(`[sca][${oldIndex}]`, `[sca][${newIndex}]`)
            }
          })

          // Update select element names (for sk and sc fields)
          clauseElement.querySelectorAll('select').forEach(select => {
            if (select.name) {
              select.name = select.name.replace(`[sca][${oldIndex}]`, `[sca][${newIndex}]`)
            }
          })

          // Update remove button display for locked date filters
          // The first pill (index 0) with locked date key should have no remove button
          const removeButton = clauseElement.querySelector('button[data-action*="removeClause"]')
          if (removeButton) {
            const shouldHideButton = this.lockedDateKey && clause.search_key === this.lockedDateKey && newIndex === 0
            removeButton.style.display = shouldHideButton ? 'none' : 'flex'
          }

          // Update _clauseIndex in multi-searchable select controllers
          // to ensure the controller dispatches events with the correct clause index
          this.updateMultiSelectClauseIndex(clauseElement, newIndex)
        }
      }
    })
  }

  // Update clause index in multi-searchable select controllers within a pill element
  updateMultiSelectClauseIndex(clauseElement, newIndex) {
    const multiSelect = clauseElement.querySelector('[data-controller*="multi-searchable-select"]')
    if (multiSelect) {
      // Update the data attribute for future controller re-connections
      multiSelect.dataset.selectMultiSearchableSelectClauseIndexValue = newIndex

      // Update the live controller's _clauseIndex if it's connected
      const controller = this.application.getControllerForElementAndIdentifier(
        multiSelect,
        'select--multi-searchable-select'
      )
      if (controller) {
        controller._clauseIndex = newIndex
      }
    }
  }

  // Ensure removing a term doesn't bubble and close the pill dropdown/modal
  removeSearchTerm(event) {
    event.preventDefault()
    event.stopPropagation()

    const searchScopeIdInput = this.element.querySelector('input[name="search_scope_id"]')
    if (searchScopeIdInput) {
      searchScopeIdInput.value = ''
    }

    super.removeSearchTerm(event)
  }

  // Override to use Bootstrap's d-none instead of Tailwind's hidden,
  // and also hide when no pill DOM nodes exist
  updateResetButtonVisibility() {
    if (!this.hasResetFiltersButtonTarget) return

    const activeClauses = this.searchClauses.filter(c => c._destroy !== 1)
    const hasRenderedPills = this.searchClauseContainerTarget.querySelector('[data-clause-index]') !== null

    if (activeClauses.length > 0 && hasRenderedPills) {
      this.resetFiltersButtonTarget.classList.remove('d-none')
      this.resetFiltersButtonTarget.classList.add('d-inline-flex')
      this.resetFiltersButtonTarget.style.display = ''
    } else {
      this.resetFiltersButtonTarget.classList.add('d-none')
      this.resetFiltersButtonTarget.classList.remove('d-inline-flex')
      this.resetFiltersButtonTarget.style.display = 'none'
    }
  }

  // Setup filter selector
  setupFilterSelector() {
    if (!this.hasFilterSelectorTarget) return

    const entries = Object.entries(this.searchOptions)
    const standardOptions = entries
      .filter(([_key, option]) => !option.custom_attribute_id)
      .map(([key, option]) => ({ id: key, name: option.title || key, group: 'Standard Filters' }))
    const customAttributeOptions = entries
      .filter(([_key, option]) => option.custom_attribute_id)
      .map(([key, option]) => ({ id: key, name: option.title || key, group: 'Custom Attributes' }))
    const options = standardOptions.concat(customAttributeOptions)

    const selectId = `filter_type_select`
    const fieldName = `filter_type`

    let html = this.renderTemplate('searchable-select-template', {
      fieldName: fieldName,
      endpoint: '',
      valueField: 'id',
      labelField: 'name',
      placeholder: 'Search filters...',
      fieldId: selectId,
      selectedValue: '',
      selectedLabel: 'Select filter...',
      dependentParam: '',
      dependentParamName: '',
      otherDependentParam: '',
      otherDependentParamName: '',
      hideSearchStyle: ''
    })

    html = html.replace(
      'data-controller="select--searchable-select"',
      `data-controller="select--searchable-select" data-options='${JSON.stringify(options).replace(/'/g, '&apos;')}'`
    )

    this.filterSelectorTarget.innerHTML = html

    setTimeout(() => {
      const hiddenSelect = this.filterSelectorTarget.querySelector(`#${selectId}`)
      if (hiddenSelect) {
        hiddenSelect.addEventListener('change', (e) => this.onFilterTypeSelected(e))
      }
    }, 0)
  }

  // Show filter selector
  showFilterSelector(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.addClauseButtonTarget.classList.remove('d-inline-flex')
    this.addClauseButtonTarget.classList.add('d-none')

    // Recreate the searchable select to ensure clean state
    this.setupFilterSelector()
    this.filterSelectorTarget.classList.remove('d-none')

    setTimeout(() => {
      const searchableSelect = this.filterSelectorTarget.querySelector('[data-controller*="select--searchable-select"]')
      if (searchableSelect) {
        const controller = this.application.getControllerForElementAndIdentifier(
          searchableSelect,
          'select--searchable-select'
        )
        if (controller && typeof controller.showDropdown === 'function') {
          controller.showDropdown()
        }
      }
    }, 50)
  }

  // Handle filter type selected
  onFilterTypeSelected(event) {
    const selectedFilterType = event.target.value

    if (!selectedFilterType || !this.searchOptions[selectedFilterType]) return

    // Create and add filter pill
    const newClause = this.initializeSearchClause({ search_key: selectedFilterType })
    this.searchClauses.push(newClause)
    const pillElement = this.createSearchClausePill(newClause, this.searchClauses.length - 1)
    this.searchClauseContainerTarget.appendChild(pillElement)

    // Scroll to show the newly added pill
    setTimeout(() => {
      this.searchClauseContainerTarget.scrollTo({
        left: this.searchClauseContainerTarget.scrollWidth,
        behavior: 'smooth'
      })
    }, 50)

    // Hide selector and show button
    this.filterSelectorTarget.classList.add('d-none')
    this.addClauseButtonTarget.classList.remove('d-none')
    this.addClauseButtonTarget.classList.add('d-inline-flex')

    // Initialize listeners and update visibility
    this.initializeSearchableSelectListeners()

    // Auto-open the pill's dropdown
    setTimeout(() => {
      this.openPillDropdown(pillElement)
    }, 50)

    this.updateResetButtonVisibility()
    this.clauseIndex++
  }

  handleComparatorChangeAndClose(event) {
    const hiddenSelect = event.target
    const match = hiddenSelect.name.match(/\[(\d+)\]/)
    if (!match) return

    const clauseIndex = parseInt(match[1])

    this.syncClauseDataFromDOM(clauseIndex)

    const clause = this.searchClauses[clauseIndex]
    if (clause) {
      const searchOption = this.searchOptions[clause.search_key]
      const newComparator = clause.search_comparator
      const oldTerms = clause.search_terms.filter(t => t._destroy !== 1)

      if (newComparator === 'is_blank') {
        clause.search_terms = [this.newSearchTerm()]
      } else if (searchOption.endpoints && searchOption.endpoints[newComparator]) {
        const hasEndpointChange = oldTerms.length > 0 && !oldTerms[0].value
        if (hasEndpointChange || oldTerms.length === 0) {
          clause.search_terms = [this.newSearchTerm()]
        }
      } else if (searchOption.type === 'date' && newComparator === 'between') {
        clause.search_terms = [{
          id: null,
          value: '',
          values: { from: '', to: '' },
          _destroy: null
        }]
      }
    }

    this.updateSearchClause(clauseIndex)

    const pillElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    if (pillElement) {
      const pillController = this.application.getControllerForElementAndIdentifier(
        pillElement.querySelector('[data-controller*="select--filter-pill"]'),
        'select--filter-pill'
      )
      if (pillController) {
        pillController.closeAllSegmentDropdowns()
      }
    }

    this.debouncedSubmitForm()
  }

  updateSearchClause(clauseIndex) {
    const clause = this.searchClauses[clauseIndex]
    if (!clause || clause._destroy === 1) return

    const existingElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    if (!existingElement) return

    this.isUpdatingClause = true

    const existingHiddenFields = existingElement.querySelectorAll(`input[type="hidden"][name*="[sca][${clauseIndex}]"]:not([name*="sta"])`)
    existingHiddenFields.forEach(field => field.remove())
    existingElement.insertAdjacentHTML('afterbegin', this.createHiddenFields(clause, clauseIndex))

    const segments = this.createPillSegments(clause, clauseIndex)

    const filterTypeDropdown = existingElement.querySelector('[data-target="select--filter-pill.filterTypeDropdown"]')
    if (filterTypeDropdown) {
      filterTypeDropdown.innerHTML = segments.filterTypeDropdownContent
    }

    const comparatorDropdown = existingElement.querySelector('[data-target="select--filter-pill.comparatorDropdown"]')
    if (comparatorDropdown) {
      comparatorDropdown.innerHTML = segments.comparatorDropdownContent
    }

    const valuesDropdown = existingElement.querySelector('[data-target="select--filter-pill.valuesDropdown"]')
    if (valuesDropdown) {

      valuesDropdown.innerHTML = segments.valuesDropdownContent

      const existingSearchTermsFields = existingElement.querySelectorAll(`input[type="hidden"][name*="[sta]"]`)
      existingSearchTermsFields.forEach(field => field.remove())
      existingElement.insertAdjacentHTML('afterbegin', this.createSearchTermsHiddenFields(clause, clauseIndex))
    }

    this.updatePillSegmentLabels(clauseIndex)

    this.initializeSearchableSelectListeners()

    this.isUpdatingClause = false
  }

  // Update pill segment labels after data change
  updatePillSegmentLabels(clauseIndex) {
    const clause = this.searchClauses[clauseIndex]
    if (!clause || clause._destroy === 1) return

    const pillElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    if (!pillElement) return

    // Update filter type label
    const filterTypeLabel = pillElement.querySelector(`[data-filter-type-label="${clauseIndex}"]`)
    if (filterTypeLabel) {
      const searchOption = this.searchOptions[clause.search_key]
      filterTypeLabel.textContent = searchOption.title
    }

    // Update comparator label
    const comparatorLabel = pillElement.querySelector(`[data-comparator-label="${clauseIndex}"]`)
    if (comparatorLabel) {
      const searchOption = this.searchOptions[clause.search_key]
      const comparator = searchOption.comparators.find(c => c[0] === clause.search_comparator)
      comparatorLabel.textContent = comparator ? comparator[1] : clause.search_comparator
    }

    // Update values label
    const valuesLabel = pillElement.querySelector(`[data-values-label="${clauseIndex}"]`)
    if (valuesLabel) {
      valuesLabel.innerHTML = this.formatValuesLabel(clause)
    }
  }

  // Open a pill's dropdown and focus first input
  openPillDropdown(pillElement) {
    const pillControllerElement = pillElement.querySelector('[data-controller*="select--filter-pill"]')
    if (!pillControllerElement) return

    // Get the pill controller
    const pillController = this.application.getControllerForElementAndIdentifier(
      pillControllerElement,
      'select--filter-pill'
    )

    if (!pillController) return

    setTimeout(() => {
      // Close other dropdowns first
      this.closeAllPillDropdowns()

      // Open the VALUES dropdown (not filter type, since filter type is already selected)
      if (pillController.hasValuesDropdownTarget && pillController.hasValuesSegmentTarget) {
        pillController.valuesDropdownTarget.classList.remove('d-none')
        pillController.positionDropdownBelowSegment(
          pillController.valuesDropdownTarget,
          pillController.valuesSegmentTarget
        )

        // Add scroll listener for this dropdown
        pillController.addScrollListenerForSegment(
          pillController.valuesDropdownTarget,
          pillController.valuesSegmentTarget
        )

        // Auto-open first searchable select (prioritize single for compatibility Make)
        setTimeout(() => {
          const searchableSelect = pillController.valuesDropdownTarget.querySelector('[data-controller*="searchable-select"]:not([data-controller*="multi-searchable-select"])')
          if (searchableSelect) {
            const controller = this.application.getControllerForElementAndIdentifier(searchableSelect, 'select--searchable-select')
            if (controller?.showDropdown) controller.showDropdown()
          } else {
            const multiSelect = pillController.valuesDropdownTarget.querySelector('[data-controller*="multi-searchable-select"]')
            if (multiSelect) {
              const controller = this.application.getControllerForElementAndIdentifier(multiSelect, 'select--multi-searchable-select')
              if (controller?.showDropdown) controller.showDropdown()
            } else {
              const selectElement = pillController.valuesDropdownTarget.querySelector('select')
              if (selectElement) {
                selectElement.focus()
                // Try to programmatically open the select using showPicker (modern browsers)
                if (typeof selectElement.showPicker === 'function') {
                  try {
                    selectElement.showPicker()
                  } catch (e) {
                    // Fallback: simulate click event
                    selectElement.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }))
                  }
                } else {
                  // Fallback for older browsers: simulate click
                  selectElement.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }))
                }
              } else {
                // If no select, focus the first input field
                const firstInput = pillController.valuesDropdownTarget.querySelector(
                  'input[type="text"], input[type="number"], input[type="date"]'
                )
                if (firstInput) {
                  firstInput.focus()
                }
              }
            }
          }
        }, 150)
      }
    }, 100)
  }

}
